import {
	BooleanField,
	DateTimeField,
	RadioField,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { StudentInjuryNoteFormat } from "../../Format";
import { StudentInjuryNoteFormValues } from "../../FormUtilities";
import { BaseFieldProps } from "../shared";
import { StudentNoteSelectField } from "../StudentNote";

/**
 * Generic props for fields of the StudentInjuryNote model.
 */
type FieldProps<K extends keyof StudentInjuryNoteFormValues.Base = keyof StudentInjuryNoteFormValues.Base> =
	BaseFieldProps<Pick<StudentInjuryNoteFormValues.Base, K>>;

/**
 * Field component for the `incidentTime` field of the StudentInjuryNote model.
 */
export function IncidentTime({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.incidentTime,
	...props
}: FieldProps<"incidentTime">) {
	return (
		<DateTimeField formState={formState} name="incidentTime" label={label} optional precision="minute" {...props} />
	);
}

/**
 * Field component for the `incidentLocation` field of the StudentInjuryNote model.
 */
export function IncidentLocation({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.incidentLocation,
	...props
}: FieldProps<"incidentLocation">) {
	return (
		<RadioField
			formState={formState}
			name="incidentLocation"
			label={label}
			options={StudentInjuryNoteFormValues.incidentLocationOptions}
			blankValue={null}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `wereOtherStudentsCauseOfInjury` field of the StudentInjuryNote model.
 */
export function WereOtherStudentsCauseOfInjury({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.wereOtherStudentsCauseOfInjury,
	...props
}: FieldProps<"wereOtherStudentsCauseOfInjury">) {
	return <BooleanField formState={formState} name="wereOtherStudentsCauseOfInjury" label={label} {...props} />;
}

/**
 * Field component for the `causeOfInjury` field of the StudentInjuryNote model.
 */
export function CauseOfInjury({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.causeOfInjury,
	...props
}: FieldProps<"causeOfInjury">) {
	return (
		<TextField
			formState={formState}
			name="causeOfInjury"
			label={label}
			optional
			multiLine
			Input={CauseAndMedicalAttentionInput}
			{...props}
		/>
	);
}

/**
 * Field component for the `medicalAttentionProvided` field of the StudentInjuryNote model.
 */
export function MedicalAttentionProvided({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.medicalAttentionProvided,
	...props
}: FieldProps<"medicalAttentionProvided">) {
	return (
		<TextField
			formState={formState}
			name="medicalAttentionProvided"
			label={label}
			optional
			multiLine
			Input={CauseAndMedicalAttentionInput}
			{...props}
		/>
	);
}

function CauseAndMedicalAttentionInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={2} />;
}

/**
 * Field component for the `adultWhoProvidedMedicalAttention` field of the StudentInjuryNote model.
 */
export function AdultWhoProvidedMedicalAttention({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.adultWhoProvidedMedicalAttention,
	...props
}: FieldProps<"adultWhoProvidedMedicalAttention">) {
	return <TextField formState={formState} name="adultWhoProvidedMedicalAttention" label={label} optional {...props} />;
}

/**
 * Field component for the `isActionTakenHomeroomTeachersNotified` field of the StudentInjuryNote model.
 */
export function IsActionTakenHomeroomTeachersNotified({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenHomeroomTeachersNotified,
	...props
}: FieldProps<"isActionTakenHomeroomTeachersNotified">) {
	return <BooleanField formState={formState} name="isActionTakenHomeroomTeachersNotified" label={label} {...props} />;
}

/**
 * Field component for the `isActionTakenParentNotifiedByPortal` field of the StudentInjuryNote model.
 */
export function IsActionTakenParentNotifiedByPortal({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotifiedByPortal,
	...props
}: FieldProps<"isActionTakenParentNotifiedByPortal">) {
	return <BooleanField formState={formState} name="isActionTakenParentNotifiedByPortal" label={label} {...props} />;
}

/**
 * Field component for the `isActionTakenParentNotifiedByPhone` field of the StudentInjuryNote model.
 */
export function IsActionTakenParentNotifiedByPhone({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotifiedByPhone,
	...props
}: FieldProps<"isActionTakenParentNotifiedByPhone">) {
	return <BooleanField formState={formState} name="isActionTakenParentNotifiedByPhone" label={label} {...props} />;
}

/**
 * Field component for the `isActionTakenParentsRequestedToCollectStudent` field of the StudentInjuryNote model.
 */
export function IsActionTakenParentsRequestedToCollectStudent({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenParentsRequestedToCollectStudent,
	...props
}: FieldProps<"isActionTakenParentsRequestedToCollectStudent">) {
	return (
		<BooleanField formState={formState} name="isActionTakenParentsRequestedToCollectStudent" label={label} {...props} />
	);
}

/**
 * Field component for the `isActionTakenParentNotificationNotNecessary` field of the StudentInjuryNote model.
 */
export function IsActionTakenParentNotificationNotNecessary({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenParentNotificationNotNecessary,
	...props
}: FieldProps<"isActionTakenParentNotificationNotNecessary">) {
	return (
		<BooleanField formState={formState} name="isActionTakenParentNotificationNotNecessary" label={label} {...props} />
	);
}

/**
 * Field component for the `isActionTakenOther` field of the StudentInjuryNote model.
 */
export function IsActionTakenOther({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.isActionTakenOther,
	...props
}: FieldProps<"isActionTakenOther">) {
	return <BooleanField formState={formState} name="isActionTakenOther" label={label} {...props} />;
}

/**
 * Field component for the `actionTakenOther` field of the StudentInjuryNote model.
 */
export function ActionTakenOther({
	formState,
	label = StudentInjuryNoteFormat.fieldLabels.actionTakenOther,
	...props
}: FieldProps<"actionTakenOther">) {
	return <TextField formState={formState} name="actionTakenOther" label={label} optional multiLine {...props} />;
}

export namespace StudentNote {
	export type SelectProps = FieldProps<"studentNoteID"> &
		Omit<StudentNoteSelectField.SelectProps<StudentInjuryNoteFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `studentNote` edge of the StudentInjuryNote model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentNoteSelectField.Select formState={formState} name="studentNoteID" {...props} />;
	}
}
